import React, { useState } from "react";
import Modal from "react-modal";
import { useDropzone } from "react-dropzone";
import "cropperjs/dist/cropper.css";
import axios from "axios";
import { getOnboardingHeaders } from "pages/NewOnboarding/shared/api";
import ProgressBar from "../ProgressBar/index";
import { Img } from "../Img/index";
import { showGlobalModal as showOldOnboardingGlobalModal } from "../GlobalModal/index";
import { useGlobalState } from "../../state/index";
import { API_CONSTANT_ONBOARDING } from "../../utils/index";
import { convertImageToWebP } from "utils/convertImageToWebP";

Modal.setAppElement("#root");

function getFileNameFromUrl(url) {
    return url.substring(url?.lastIndexOf('/') + 1);
}

const icons = {
    'jpg': '📷', 'jpeg': '📷', 'png': '🖼️', 'gif': '🎞️',
    'pdf': '📄', 'doc': '📝', 'docx': '📝', 'txt': '📜',
    'ppt': '📊', 'pptx': '📊', 'xls': '📄', 'xlsx': '📄',
    'mp3': '🎵', 'wav': '🎶', 'mp4': '🎥', 'mov': '🎬',
    'zip': '📦', 'rar': '📦', '7z': '📦', 'exe': '⚙️',
    'html': '🌐', 'css': '🎨', 'js': '📜', 'json': '🔢',
    'csv': '📄', 'xml': '📄', 'php': '🐘', 'py': '🐍',
    'java': '☕', 'c': '🔣', 'cpp': '🔣', 'rb': '💎'
};

type ImageSet = {
    orignalImage: String;
    selectedImage: String;
    enhancedImage: String;
}

const ImageUpload2: React.FC<{
    isDownloadable?: boolean;
    onChange: (file: object) => void;
    onMultiDelete?: (file: object) => void;
    imageValue?: {
        orignalImage: String;
        selectedImage: String;
        enhancedImage: String;
    };
    label?: string;
    customMessage?: string;
    wantHDOption?: boolean;
    previewNotRequired?: boolean;
    disable?: boolean;
    labelClassNames?: string;
    containerHeight?: string | any; // New prop for flexible height
    maxSize?: number;
    isError?: boolean;
    multiImageValues?: ImageSet[];
    accept?: any;
    processExcel?: boolean
}> = ({
    onChange,
    onMultiDelete,
    imageValue,
    label,
    customMessage,
    wantHDOption,
    disable,
    maxSize = 5,
    isError,
    multiImageValues,
    accept,
    processExcel,
    isDownloadable
}) => {

        const { instData } = useGlobalState();

        const [modalIsOpen, setModalIsOpen] = useState(false);
        const [loader, setLoader] = useState<boolean>(false);
        const [uploadController, setUploadController] = useState<AbortController | null>(null);

        const IS_ORIGNAL_IMAGE = Boolean(
            imageValue?.selectedImage === imageValue?.orignalImage
        );
        const IS_ENHANCED_IMAGE = Boolean(
            imageValue?.selectedImage === imageValue?.enhancedImage
        );

        const handleUpload = async (file: File) => {

            // Create a new AbortController
            const controller = new AbortController();
            setUploadController(uploadController => controller);

            setLoader(loader => true);

            try {
                const uploadFile = await convertImageToWebP(file);
                const formData = new FormData();
                formData.append(processExcel ? "file" : "files", uploadFile);

                let url = `${API_CONSTANT_ONBOARDING}/master/upload/tempfiles`;

                if (processExcel) {
                    url = `${API_CONSTANT_ONBOARDING}/data/process-excel?branch_id=${instData?.BRANCH_ID}&institute_id=${instData?.INSTITUTE_ID}&database_name=${instData?.DATABASE_NAME}`;
                }

                const response = await axios.post(url,
                    formData,
                    {
                        headers: {
                            ...getOnboardingHeaders(instData),
                            "Custom-Header": "multipart/form-data",
                            "academic-year-id": instData?.ACADEMIC_YEAR_ID,
                        },
                        signal: controller.signal, // Attach the signal for cancellation
                    }
                );
                if ([200, 201]?.includes(+response?.data?.code)) {
                    if (processExcel) {
                        onChange({
                            orignalImage: (response?.data?.data?.excel_file_url || ""),
                            selectedImage: (response?.data?.data?.excel_file_url || ""),
                            enhancedImage: "",
                        });
                    } else {
                        onChange({
                            orignalImage: response?.data?.data?.[0],
                            selectedImage: response?.data?.data?.[0],
                            enhancedImage: "",
                        });
                    }
                } else {
                    showOldOnboardingGlobalModal(response?.data?.message || "Upload failed!", "error");
                }
                // setModalIsOpen(modalIsOpen => true);
            } catch (error: any) {
                if (axios.isCancel(error)) {
                    showOldOnboardingGlobalModal(error.message || "Upload failed!", "error");
                    console.log("Upload aborted:", error.message);
                } else {
                    showOldOnboardingGlobalModal(error?.response?.data?.message || "Upload failed!", "error");
                }
            } finally {
                setLoader(loader => false);
            }
        };

        // Function to cancel the request
        const handleCancelUpload = () => {
            if (uploadController) {
                uploadController.abort();
                setUploadController(uploadController => null);
                setLoader(loader => false);
            }
        };

        const handleSelection = (url) => {
            // setSelectedImage(selectedImage => url);
            onChange({
                ...imageValue,
                selectedImage: url,
            });
        };

        const handleModalCancel = () => {
            // setEnhancedImage(enhancedImage => null);
            setModalIsOpen((modalIsOpen) => false);
        };

        const handleSaveEnhancedImage = () => {
            // onChange(selectedImage);
            // setEnhancedImage(null);
            setModalIsOpen(false);
        };

        const onDrop = (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                handleUpload(file);
            }
        };

        const handleEnhanceImage = async () => {
            if (!imageValue?.orignalImage) return;
            try {
                setLoader((loader) => true);
                const response = await axios.post(
                    `${API_CONSTANT_ONBOARDING}/master/enhance-image`,
                    { image_url: imageValue?.orignalImage, preset: "auto_enhance" }
                );
                if (!response?.data?.data?.result_url) {
                    showOldOnboardingGlobalModal(response?.data?.message, "error");
                } else {
                    // setEnhancedImage(enhancedImage => response?.data?.data?.result_url);
                    onChange({
                        ...imageValue,
                        enhancedImage: response?.data?.data?.result_url,
                    });
                }
                setLoader((loader) => false);
            } catch (err) {
                setLoader((loader) => false);
                showOldOnboardingGlobalModal(err?.data?.message || err?.message, "error");
            }
        };

        const handleDeleteTheUploadedFile = () => {
            onChange({
                orignalImage: "",
                selectedImage: "",
                enhancedImage: "",
            });
        }

        const handleDeleteTheUploadedMultiFile = (index) => {
            onMultiDelete(index);
        }

        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop,
            accept: accept || { "image/*": [] }, // not gif
            multiple: false,
            disabled: disable,
        });

        return (
            <>
                <div className="w-full space-y-2">
                    <div
                        {...getRootProps()}
                        className={`
          relative 
          flex 
          flex-col 
          items-center 
          justify-center
          px-2 
          py-2
          border 
          border-solid 
          ${isError ? "border-red-500" : "border-gray-300"}
          rounded-lg 
          bg-[white]
          overflow-hidden 
          ${isDragActive ? " bg-blue-100" : ""}
        `}
                    >
                        <input {...getInputProps()} />
                        <div className="w-full">
                            <div className="flex flex-col justify-center items-center gap-4 h-[30vh]">
                                <Img src={"/images/uploadCloud.svg"} alt="image" className="min-w-10 min-h-10 max-w-10 max-h-10 object-contain rounded-full" />
                                {!!label && <p className="text-[black]">{label}</p>}
                                <p className="text-sm">Choose a file (e.g., CSV, Excel) to import student details</p>
                            </div>
                        </div>
                    </div>
                </div>
                {(!!multiImageValues?.length || loader) && <div className="w-full space-y-2 my-4">
                    <p className="text-[black] font-medium text-sm">Your File</p>
                    {loader && <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-4">
                        <ProgressBar />
                        <button onClick={handleCancelUpload} type="button"><Img src="/images/new-landing-page/blackCloseButton.svg" alt="blackCloseButton" className="object-contain" /></button>
                    </div>}
                    {multiImageValues?.map((item: any, index) => <div key={"asdasdasdasdasdasdasdasda" + index} className="flex justify-between items-center gap-2 border border-gray-300 rounded-lg p-4">
                        <div className="flex gap-2 items-center">
                            {typeof (icons?.[getFileNameFromUrl(item?.selectedImage)?.split(".")?.[1]]) === "string" ? <p className="text-xl">{icons?.[getFileNameFromUrl(item?.selectedImage)?.split(".")?.[1]]}</p> : ""}
                            <p className="text-xs">{getFileNameFromUrl(item?.selectedImage)}</p>
                        </div>
                        <button onClick={() => {
                            if (isDownloadable) {
                                window.open(item?.selectedImage, "_blank")
                            } else {
                                handleDeleteTheUploadedMultiFile(index)
                            }
                        }} type="button">
                            {isDownloadable ? <Img src={"/images/download.svg"} className="object-contain" /> :
                                <Img src="/images/new-landing-page/delete.svg" alt="delete" className="object-contain" />}
                        </button>
                    </div>)}
                </div>}
            </>
        );
    };

export default ImageUpload2;
